import { useCallback, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Checkbox, FormControlLabel } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CloseIcon from "@mui/icons-material/Close";

import { BREAKPOINTS } from "../../../utils/constants";
import { useResponsive } from "../../../hooks/useResponsive";
import { useEscapeKey, useModalA11y } from "../../../hooks";
import Button, { ButtonTypes } from "../../Button";
import { IconButton } from "../../IconButton";
import {
  ConfirmDetails,
  CONFIRM_BUTTON_TYPE,
  ErrorBanner,
  IconBadge,
  Title,
  Description,
  DontAskWrapper,
  DialogBackdrop,
  Dialog,
  DialogBody,
  DialogHeader,
  HeadingBlock,
  DialogFooter,
  CloseSlot,
  SheetBackdrop,
  SheetGroup,
  SheetCard,
  SheetBody,
  SheetActionButton,
  SheetCancelButton,
} from "../confirmShared";
import { ConfirmPopUpProps } from "./types";

const modalElement = document.getElementById("modal-root") as HTMLElement;

/**
 * Generic confirmation modal engine. Renders a centered dialog on wide viewports
 * and a bottom action sheet on small ones, with a shared tone system, an
 * optional structured record preview, and an optional "don't ask again" toggle.
 * `DeletePopUp` is the destructive (`tone="danger"`) preset over this component;
 * status and edit confirmations use it directly.
 */
export const ConfirmPopUp: React.FC<ConfirmPopUpProps> = ({
  onConfirm,
  isOpen,
  setIsOpen,
  title,
  text,
  details,
  tone = "warning",
  icon,
  confirmLabel = "Confirm",
  loadingLabel = "Working…",
  cancelLabel = "Cancel",
  showDontAskAgain = false,
  dontAskAgainKey,
  isLoading = false,
  isConfirmDisabled = false,
  errorMessage,
}) => {
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const describedBy = text ? descriptionId : undefined;

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setIsOpen(false);
    }
  }, [isLoading, setIsOpen]);

  useEscapeKey(handleClose, isOpen);
  useModalA11y(isOpen, containerRef);

  const handleConfirm = () => {
    if (showDontAskAgain && dontAskAgain && dontAskAgainKey) {
      localStorage.setItem(dontAskAgainKey, "true");
    }
    onConfirm();
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const dontAskAgainControl = showDontAskAgain && (
    <DontAskWrapper>
      <FormControlLabel
        control={
          <Checkbox
            checked={dontAskAgain}
            onChange={(e) => setDontAskAgain(e.target.checked)}
            disabled={isLoading}
            sx={{ color: "var(--color-select-text)" }}
          />
        }
        label="Don't ask me again"
      />
    </DontAskWrapper>
  );

  const badge = (
    <IconBadge $tone={tone}>{icon ?? <WarningAmberRoundedIcon />}</IconBadge>
  );

  const confirmText = isLoading ? loadingLabel : confirmLabel;
  const confirmDisabled = isLoading || isConfirmDisabled;

  const errorBanner = errorMessage && (
    <ErrorBanner role="alert">
      <ErrorOutlineRoundedIcon aria-hidden="true" />
      <span>{errorMessage}</span>
    </ErrorBanner>
  );

  const renderMobile = () => (
    <SheetBackdrop onClick={handleBackdropClick}>
      <SheetGroup
        ref={containerRef}
        tabIndex={-1}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
      >
        <SheetCard>
          <SheetBody>
            {badge}
            <Title id={titleId}>{title}</Title>
            <Description id={descriptionId}>{text}</Description>
            {details && <ConfirmDetails detail={details} />}
            {errorBanner}
            {dontAskAgainControl}
          </SheetBody>
          <SheetActionButton
            $tone={tone}
            onClick={handleConfirm}
            disabled={confirmDisabled}
            aria-label={confirmLabel}
          >
            {confirmText}
          </SheetActionButton>
        </SheetCard>
        <SheetCancelButton
          onClick={handleClose}
          disabled={isLoading}
          aria-label={cancelLabel}
        >
          {cancelLabel}
        </SheetCancelButton>
      </SheetGroup>
    </SheetBackdrop>
  );

  const renderDesktop = () => (
    <DialogBackdrop onClick={handleBackdropClick}>
      <Dialog
        ref={containerRef}
        tabIndex={-1}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
      >
        <CloseSlot>
          <IconButton
            onClick={handleClose}
            disabled={isLoading}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </CloseSlot>
        <DialogBody>
          <DialogHeader>
            {badge}
            <HeadingBlock>
              <Title id={titleId}>{title}</Title>
              <Description id={descriptionId}>{text}</Description>
            </HeadingBlock>
          </DialogHeader>
          {details && <ConfirmDetails detail={details} />}
          {errorBanner}
          {dontAskAgainControl}
        </DialogBody>
        <DialogFooter>
          <Button
            type={CONFIRM_BUTTON_TYPE[tone]}
            onClick={handleConfirm}
            disabled={confirmDisabled}
            aria-label={confirmLabel}
          >
            {confirmText}
          </Button>
          <Button
            type={ButtonTypes.cancel}
            onClick={handleClose}
            disabled={isLoading}
            aria-label={cancelLabel}
          >
            {cancelLabel}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogBackdrop>
  );

  const modalContent = isCompact ? renderMobile() : renderDesktop();

  return createPortal(isOpen ? modalContent : null, modalElement);
};
